import { StatusTicketCard } from '../models/StatusTicketCard';
import { FlightTicketTone } from './flight-ticket-frame.component';

export function ticketTone(status: StatusTicketCard): FlightTicketTone {
  switch (status) {
    case StatusTicketCard.CANCELLED: return 'cancelled';
    case StatusTicketCard.INACTIVE: return 'inactive';
    default: return 'active';
  }
}

export interface ItineraryLeg {
  origin: string;
  destination: string;
  departure: string;
  arrival: string;
}

/** The fields of a flight segment that the itinerary helpers read. */
export interface SegmentLike {
  originLocationCode: string;
  destinationLocationCode: string;
  departureDateTime: string;
  arrivalDateTime: string;
}

/** Legs of a search result or booked ticket, in travel order. */
export function toLegs(segments: readonly SegmentLike[]): ItineraryLeg[] {
  return segments.map(segment => ({
    origin: segment.originLocationCode,
    destination: segment.destinationLocationCode,
    departure: segment.departureDateTime,
    arrival: segment.arrivalDateTime
  }));
}

/** "BSB 07:05 → MAO 15:35": first departure and last arrival of an itinerary; empty without legs. */
export function routeSummary(legs: readonly ItineraryLeg[]): string {
  if (!legs.length) return '';
  const schedule = headerSchedule(legs);
  const first = legs[0];
  const last = legs[legs.length - 1];
  return [`${first.origin} ${schedule.departureTime}`.trim(), `${last.destination} ${schedule.arrivalTime}`.trim()].join(' → ');
}

export interface ItinerarySummary {
  /** Airport codes in travel order: origin plus every arrival. */
  stops: string[];
  /** Airport the trip is going to, or null when it cannot be determined safely. */
  destination: string | null;
  roundTrip: boolean;
}

/** A stop at least this long is treated as a stay rather than a connection. */
const MAX_CONNECTION_MS = 24 * 60 * 60 * 1000;

export interface SummarizeOptions {
  /**
   * Treat a short stop that changes airport (lands at CGH, leaves from GRU) as a connection.
   * Used for a single card, which shows one trip.
   */
  allowAirportChange?: boolean;
}

/**
 * Works out where an itinerary is going without assuming that the last arrival is the destination:
 * - a single leg goes to its arrival;
 * - a path that comes back the same way (GRU → MAO → GRU) goes to its turnaround airport;
 * - continuous legs with short stops are connections and go to the last arrival;
 * - anything else (other loops, long stays, gaps, unparseable dates) returns null → fallback image.
 */
export function summarizeItinerary(legs: readonly ItineraryLeg[], options: SummarizeOptions = {}): ItinerarySummary {
  if (!legs.length) return { stops: [], destination: null, roundTrip: false };

  const stops = [legs[0].origin, ...legs.map(leg => leg.destination)];
  if (legs.length === 1) return { stops, destination: legs[0].destination, roundTrip: false };

  const continuous = legs.every((leg, i) => i === 0 || sameAirport(leg.origin, legs[i - 1].destination));
  if (!continuous && !options.allowAirportChange) return { stops, destination: null, roundTrip: false };

  if (sameAirport(stops[0], stops[stops.length - 1])) {
    if (!continuous) return { stops, destination: null, roundTrip: false };
    const mirrored = stops.length % 2 === 1 && stops.every((code, i) => code === stops[stops.length - 1 - i]);
    return { stops, destination: mirrored ? stops[(stops.length - 1) / 2] : null, roundTrip: mirrored };
  }

  for (let i = 1; i < legs.length; i++) {
    const arrived = parseLocalDateTime(legs[i - 1].arrival);
    const leaves = parseLocalDateTime(legs[i].departure);
    if (arrived === null || leaves === null || leaves - arrived >= MAX_CONNECTION_MS) {
      return { stops, destination: null, roundTrip: false };
    }
  }

  return { stops, destination: stops[stops.length - 1], roundTrip: false };
}

/**
 * Destination shown on every card (search, active, cancelled, history).
 * One trip per card, so a short change of airport still ends at the last arrival.
 */
export function summarizeCardItinerary(legs: readonly ItineraryLeg[]): ItinerarySummary {
  return summarizeItinerary(legs, { allowAirportChange: true });
}

export interface ConnectionStopView {
  code: string;
  /** Arrival at the connection airport and departure of the next leg, e.g. "21:45–23:00". */
  span: string;
}

export interface HeaderSchedule {
  departureTime: string;
  arrivalTime: string;
  arrivesNextDay: boolean;
  connectionStops: ConnectionStopView[];
}

/** Clock times and real connection stops for the photo header. Direct flights have no stops. */
export function headerSchedule(legs: readonly ItineraryLeg[]): HeaderSchedule {
  if (!legs.length) {
    return { departureTime: '', arrivalTime: '', arrivesNextDay: false, connectionStops: [] };
  }

  const first = legs[0];
  const last = legs[legs.length - 1];
  const departureDay = dayOf(first.departure);
  const arrivalDay = dayOf(last.arrival);

  return {
    departureTime: clockOf(first.departure),
    arrivalTime: clockOf(last.arrival),
    arrivesNextDay: !!departureDay && !!arrivalDay && departureDay !== arrivalDay,
    connectionStops: legs.slice(0, -1).flatMap((leg, index) => {
      const code = leg.destination?.trim();
      if (!code) return [];
      return [{ code, span: `${clockOf(leg.arrival)}–${clockOf(legs[index + 1].departure)}` }];
    })
  };
}

function sameAirport(a: string | null | undefined, b: string | null | undefined): boolean {
  const code = a?.trim().toUpperCase();
  return !!code && code === b?.trim().toUpperCase();
}

function clockOf(value: string | null | undefined): string {
  return value && value.length >= 16 ? value.slice(11, 16) : '';
}

function dayOf(value: string | null | undefined): string {
  return value?.slice(0, 10) ?? '';
}

function parseLocalDateTime(value: string | null | undefined): number | null {
  if (!value) return null;
  const time = new Date(value.trim().replace(' ', 'T')).getTime();
  return Number.isNaN(time) ? null : time;
}

export type SeatSummaryState = 'loading' | 'unavailable' | 'empty' | 'available';

export interface SeatSummary {
  state: SeatSummaryState;
  label: string;
}

/**
 * Seats for the whole itinerary, following the same states as <app-seat-availability>
 * (undefined → Consultando, null → Indisponível, 0 → Sem assentos, n → n assentos).
 * With several segments the scarcest one decides, since a seat is needed on every segment.
 */
export function summarizeSeats(items: readonly { availableSeats?: number | null }[]): SeatSummary | null {
  if (!items.length) return null;
  if (items.some(item => item.availableSeats === undefined)) return { state: 'loading', label: 'Consultando' };
  if (items.some(item => item.availableSeats === null)) return { state: 'unavailable', label: 'Indisponível' };

  const seats = Math.min(...items.map(item => item.availableSeats as number));
  if (seats <= 0) return { state: 'empty', label: 'Sem assentos' };
  return { state: 'available', label: seats === 1 ? '1 assento' : `${seats} assentos` };
}

/** "3h 40min" for a whole number of minutes; empty for zero or invalid values. */
export function formatMinutes(totalMinutes: number): string {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return '';
  const minutes = Math.round(totalMinutes);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest}min`;
  return rest ? `${hours}h ${rest}min` : `${hours}h`;
}

/**
 * Human form of the API duration ("03:40" → "3h 40min"). Anything that is not HH:mm
 * is returned trimmed, so nothing is invented when the backend sends another format.
 */
export function formatDuration(value: string | null | undefined): string {
  const text = value?.trim() ?? '';
  const match = /^(\d{1,3}):(\d{2})$/.exec(text);
  if (!match) return text;
  return formatMinutes(Number(match[1]) * 60 + Number(match[2]));
}

/**
 * Waiting time at a connection airport. Only safe when the next leg leaves from the
 * airport the previous one reached (same local clock); otherwise null.
 */
export function connectionWait(arrivalAt: string, arrivalCode: string, departureAt: string, departureCode: string): string | null {
  if (!arrivalCode || arrivalCode !== departureCode) return null;
  const arrived = parseLocalDateTime(arrivalAt);
  const leaves = parseLocalDateTime(departureAt);
  if (arrived === null || leaves === null || leaves <= arrived) return null;
  return formatMinutes((leaves - arrived) / 60000) || null;
}
